import { Container, Divider, ListItemText, Paper, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { PluginsForm } from '../plugins-form'
import { useTranslation } from 'react-i18next'
import { SectionHeader } from '@sitoo/mui-components'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetSwishPluginDocument,
  PluginSwish,
  UpdateSwishDocument,
} from '../../../../generated/graphql'
import { SwitchInput } from '../../../../inputs/switch-input'
import { ListItem } from '@mui/material'
import { useMemo } from 'react'
import { formatDecimalInput } from '../../../../utils/format/number'
import { useMoney } from '../../../../hooks/money'

type Form = PluginSwish

export const SettingsSwishPage = () => {
  const { t } = useTranslation(['apps', 'shared'])

  const formContext = useForm<Form>({
    defaultValues: {
      payee_alias: '',
      message_payment: '',
      message_refund: '',
      moneylimitmin: null,
      selfcheckoutenabled: false,
      certificate_public: '',
      certificate_private: '',
    },
  })

  const { control } = formContext

  const { formatCurrency } = useMoney()

  const { data } = useQuery(GetSwishPluginDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const [updateSettings, { loading: isLoading }] =
    useMutation(UpdateSwishDocument)

  const onSubmit = async (formData: Form) => {
    const { data } = await updateSettings({
      variables: {
        data: {
          ...formData,
          moneylimitmin: formData.moneylimitmin
            ? formatDecimalInput(formData.moneylimitmin)
            : null,
        },
      },
    })

    return data?.updateSwish
  }

  const serverData = useMemo(
    () => ({
      ...data?.swishPlugin,
      moneylimitmin: data?.swishPlugin?.moneylimitmin
        ? formatCurrency(data?.swishPlugin?.moneylimitmin, {
            hideCurrency: true,
            useGrouping: false,
          })
        : null,
    }),
    [data?.swishPlugin, formatCurrency],
  )

  return (
    <Container>
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={serverData}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:swish.settings_tab')}
            </SectionHeader>

            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <TextFieldInput
                name="payee_alias"
                control={control}
                label={t('apps:swish.payee_alias_label')}
                helperText={t('apps:swish.payee_alias_helper')}
                fullWidth
              />

              <TextFieldInput
                name="message_payment"
                control={control}
                label={t('apps:swish.user_payment_message_label')}
                helperText={t('apps:swish.user_payment_message_helper')}
                fullWidth
                rules={{
                  maxLength: {
                    value: 50,
                    message: t('shared:validation.max_length', {
                      amount: 50,
                      field: t('apps:swish.user_payment_message_label'),
                    }),
                  },
                }}
              />

              <TextFieldInput
                name="message_refund"
                control={control}
                label={t('apps:swish.refund_message_label')}
                helperText={t('apps:swish.refund_message_helper')}
                fullWidth
                rules={{
                  maxLength: {
                    value: 50,
                    message: t('shared:validation.max_length', {
                      amount: 50,
                      field: t('apps:swish.refund_message_label'),
                    }),
                  },
                }}
              />

              <TextFieldInput
                name="moneylimitmin"
                control={control}
                label={t('apps:swish.limit_amount_label')}
                helperText={t('apps:swish.limit_amount_helper')}
                inputMode="numeric"
                fullWidth
                rules={{ min: 1 }}
                transform={{
                  input: (value) =>
                    value === null || value === undefined ? '' : String(value),
                  output: (event) => event.currentTarget.value || null,
                }}
              />
            </Stack>
            <Divider />
            <ListItem
              component="div"
              secondaryAction={
                <SwitchInput
                  control={control}
                  name="selfcheckoutenabled"
                  aria-label={t('apps:swish.activate_self_checkout_label')}
                />
              }
            >
              <ListItemText
                primary={t('apps:swish.activate_self_checkout_label')}
                secondary={t('apps:swish.activate_self_checkout_helper')}
              />
            </ListItem>
          </Paper>

          <Paper>
            <SectionHeader variant="transparent">
              {t('apps:swish.authentication_section')}
            </SectionHeader>
            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <TextFieldInput
                name="certificate_public"
                control={control}
                label={t('apps:swish.public_key_label')}
                helperText={t('apps:swish.public_key_helper')}
                fullWidth
                multiline
                minRows={3}
                maxRows={30}
                transform={{
                  input: (value) => (value ? String(value) : ''),
                  output: (event) => event.currentTarget.value || null,
                }}
              />

              <TextFieldInput
                name="certificate_private"
                control={control}
                label={t('apps:swish.private_key_label')}
                helperText={t('apps:swish.private_key_helper')}
                fullWidth
                multiline
                minRows={3}
                maxRows={30}
                transform={{
                  input: (value) => (value ? String(value) : ''),
                  output: (event) => event.currentTarget.value || null,
                }}
              />
            </Stack>
          </Paper>
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
